// Single-voice narrator for the Explain workflow. Only one utterance is ever
// in flight, so the module owns the browser's speechSynthesis queue outright:
// every new request cancels the previous one and invalidates its callbacks.

// Chromium silently stops a synthesis job that has been running for roughly
// 15 seconds. Nudging pause/resume before that ceiling keeps long tutor
// replies audible to the end.
const KEEP_ALIVE_INTERVAL_MS = 10000

let activeToken = 0
let keepAliveTimer = null

export const isSpeechSupported = () =>
  typeof window !== 'undefined' &&
  'speechSynthesis' in window &&
  typeof window.SpeechSynthesisUtterance === 'function'

const isEnglishLang = (lang) =>
  typeof lang === 'string' && lang.toLowerCase().startsWith('en')

const voiceKey = (voice) => {
  if (typeof voice?.voiceURI === 'string' && voice.voiceURI) return voice.voiceURI
  return `${voice?.name || ''}|${voice?.lang || ''}`
}

const formatVoiceLabel = (voice) => {
  const name = String(voice?.name || 'Voice').trim() || 'Voice'
  const lang = String(voice?.lang || '').trim()
  if (!lang) return name
  if (name.toLowerCase().includes(lang.toLowerCase())) return name
  return `${name} (${lang})`
}

/**
 * English voices first (this product narrates English math). If the engine
 * has none, every voice is returned so the picker is never an empty trap.
 */
export const listSpeechVoices = () => {
  if (!isSpeechSupported()) return []
  const raw = window.speechSynthesis.getVoices()
  if (!Array.isArray(raw) || raw.length === 0) return []

  const english = raw.filter((voice) => isEnglishLang(voice.lang))
  const source = english.length > 0 ? english : raw

  const seen = new Set()
  const voices = []
  for (const voice of source) {
    const uri = voiceKey(voice)
    if (!uri || seen.has(uri)) continue
    seen.add(uri)
    voices.push({
      uri,
      name: String(voice.name || 'Voice'),
      lang: String(voice.lang || ''),
      localService: voice.localService === true,
      label: formatVoiceLabel(voice)
    })
  }

  voices.sort((a, b) => {
    const aUs = a.lang.toLowerCase() === 'en-us' ? 0 : 1
    const bUs = b.lang.toLowerCase() === 'en-us' ? 0 : 1
    if (aUs !== bUs) return aUs - bUs
    if (a.localService !== b.localService) return a.localService ? -1 : 1
    return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
  })

  return voices
}

/**
 * Picks the voice to narrate with. This module owns no preference storage —
 * the caller passes whatever the student saved (see
 * helpers/explainNarrationPrefs) and gets a sensible device default back when
 * that voice is missing or unset.
 */
export const resolveSpeechVoiceUri = (voices, preferredUri = '') => {
  if (!Array.isArray(voices) || voices.length === 0) return ''
  if (preferredUri && voices.some((voice) => voice.uri === preferredUri)) {
    return preferredUri
  }

  const usLocal = voices.find((voice) => (
    voice.lang.toLowerCase() === 'en-us' && voice.localService
  ))
  if (usLocal) return usLocal.uri
  const us = voices.find((voice) => voice.lang.toLowerCase() === 'en-us')
  if (us) return us.uri
  return voices[0].uri
}

export const subscribeSpeechVoices = (onChange) => {
  if (!isSpeechSupported() || typeof onChange !== 'function') {
    return () => {}
  }

  const emit = () => {
    onChange(listSpeechVoices())
  }

  emit()
  const synth = window.speechSynthesis
  if (typeof synth.addEventListener === 'function') {
    synth.addEventListener('voiceschanged', emit)
  } else {
    synth.onvoiceschanged = emit
  }
  // Chromium often populates the list one tick after the first getVoices().
  const timeoutId = window.setTimeout(emit, 0)

  return () => {
    window.clearTimeout(timeoutId)
    if (typeof synth.removeEventListener === 'function') {
      synth.removeEventListener('voiceschanged', emit)
    } else if (synth.onvoiceschanged === emit) {
      synth.onvoiceschanged = null
    }
  }
}

const findEngineVoice = (uri) => {
  if (!isSpeechSupported() || typeof uri !== 'string' || !uri) return null
  const all = window.speechSynthesis.getVoices()
  if (!Array.isArray(all)) return null
  return all.find((voice) => voice.voiceURI === uri)
    || all.find((voice) => voiceKey(voice) === uri)
    || null
}

// LaTeX read literally ("backslash frac open brace") is worse than no audio at
// all, so the common notation a step explanation can contain is spelled out.
export const toSpokenText = (text) => {
  if (typeof text !== 'string') return ''

  return text
    .replace(/\$+/g, ' ')
    .replace(/\\[()[\]]/g, ' ')
    .replace(/\*\*/g, ' ')
    .replace(/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, ' $1 over $2 ')
    .replace(/\\sqrt\s*\{([^{}]*)\}/g, ' the square root of $1 ')
    .replace(/\\(times|cdot)(?![a-zA-Z])/g, ' times ')
    .replace(/\\div(?![a-zA-Z])/g, ' divided by ')
    .replace(/\\pm(?![a-zA-Z])/g, ' plus or minus ')
    .replace(/\\leq?(?![a-zA-Z])/g, ' is less than or equal to ')
    .replace(/\\geq?(?![a-zA-Z])/g, ' is greater than or equal to ')
    .replace(/\\neq(?![a-zA-Z])/g, ' is not equal to ')
    .replace(/\\approx(?![a-zA-Z])/g, ' is about ')
    .replace(/\\pi(?![a-zA-Z])/g, ' pi ')
    .replace(/\^\s*\{?\s*2\s*\}?(?![0-9])/g, ' squared ')
    .replace(/\^\s*\{?\s*3\s*\}?(?![0-9])/g, ' cubed ')
    .replace(/\^/g, ' to the power of ')
    .replace(/\\[a-zA-Z]+/g, ' ')
    .replace(/[{}\\_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const stopKeepAlive = () => {
  if (keepAliveTimer !== null) {
    clearInterval(keepAliveTimer)
    keepAliveTimer = null
  }
}

export const cancelSpeech = () => {
  // Bumping the token first means any in-flight utterance's end/error handler
  // becomes a no-op, so a cancel can never clear a newer utterance's state.
  activeToken += 1
  stopKeepAlive()
  if (!isSpeechSupported()) return
  window.speechSynthesis.cancel()
}

/**
 * Speaks a single piece of text, replacing whatever was playing.
 * @param {string} text Raw explanation or tutor reply.
 * @param {{onStart?: Function, onEnd?: Function, voiceUri?: string}} handlers
 *   Lifecycle callbacks; only fired while this utterance is the active one.
 *   `voiceUri` selects a browser voice; omitted uses the engine default.
 * @return {boolean} True when playback was handed to the browser.
 */
export const speak = (text, { onStart, onEnd, voiceUri } = {}) => {
  if (!isSpeechSupported()) return false

  const spoken = toSpokenText(text)
  if (!spoken) return false

  cancelSpeech()
  const token = activeToken

  const utterance = new window.SpeechSynthesisUtterance(spoken)
  const selectedVoice = findEngineVoice(voiceUri)
  if (selectedVoice) {
    utterance.voice = selectedVoice
    utterance.lang = selectedVoice.lang || 'en-US'
  } else {
    utterance.lang = 'en-US'
  }
  utterance.rate = 0.95
  utterance.pitch = 1
  utterance.volume = 1

  const finish = () => {
    if (token !== activeToken) return
    stopKeepAlive()
    if (onEnd) onEnd()
  }
  utterance.onstart = () => {
    if (token !== activeToken) return
    if (onStart) onStart()
  }
  utterance.onend = finish
  utterance.onerror = finish

  try {
    window.speechSynthesis.speak(utterance)
  } catch (error) {
    console.error('Error starting narration:', error)
    return false
  }

  keepAliveTimer = setInterval(() => {
    const synth = window.speechSynthesis
    if (token !== activeToken || !synth.speaking) {
      stopKeepAlive()
      return
    }
    synth.pause()
    synth.resume()
  }, KEEP_ALIVE_INTERVAL_MS)

  return true
}
